import React, {
  Fragment,
  useRef,
  useMemo,
  useState,
  useEffect,
  useCallback,
  useImperativeHandle
} from 'react'
import PropTypes from 'prop-types'

import { isEqual, get, isUndefined, isEmpty, isArray, first } from 'lodash'

import cx from 'classnames'

import { FixedSizeList as List } from 'react-window'

import CustomInput from '../CustomInput'

import AutocompleteOption from './AutocompleteOption'

import './index.scss'

const useOnClickOutside = (ref, handler) => {
  useEffect(() => {
    const listener = (event) => {
      if (!ref.current || ref.current.contains(event.target)) {
        return
      }
      handler(event)
    }
    document.addEventListener('mousedown', listener)
    document.addEventListener('keydown', listener)
    document.addEventListener('keyup', listener)
    document.addEventListener('keypress', listener)
    document.addEventListener('touchstart', listener)

    return () => {
      document.removeEventListener('mousedown', listener)
      document.removeEventListener('keydown', listener)
      document.removeEventListener('keyup', listener)
      document.removeEventListener('keypress', listener)
      document.removeEventListener('touchstart', listener)
    }
  }, [ref, handler])
}

const Autocomplete = React.forwardRef(
  (
    {
      options,
      items = options,
      labelKey: labelKeys = 'label',
      valueKey = 'value',
      defaultValue = null,
      renderItem,
      onChange,
      onChangeValue,
      onBlur,
      onFocus,
      onKeyDown,
      className,
      value,
      getLabel,
      disabled,
      children,
      ...props
    },
    ref
  ) => {
    const inputRef = useRef(null)
    const virtualListRef = useRef(null)
    const optionsContainerRef = useRef(null)
    const autoCompleteWrapperRef = useRef(null)

    const [search, setSearch] = useState('')
    const [display, setDisplay] = useState(false)
    const [currentFocusIndex, setCurrentFocusIndex] = useState(-1)
    const [currentValueObject, setCurrentValueObject] = useState(defaultValue)

    const labelKey = isArray(labelKeys) ? labelKeys[0] : labelKeys

    const filterItems = (item, search, labelKeys) => {
      if (isArray(labelKeys)) {
        let combinationString = ''

        labelKeys.forEach((key, i) => {
          combinationString +=
            item[key].toLowerCase() + addSpaceBetweenKeys(labelKeys, i)
        })

        return combinationString.includes(search.toLowerCase())
      } else {
        // old way with labelKeys as string (not array)
        return getLabel(item[labelKeys] || '')
          .toLowerCase()
          .includes(search.toLowerCase())
      }
    }

    const filteredItems = useMemo(() => {
      return (items || []).filter((item) =>
        filterItems(item, search, labelKeys)
      )
    }, [items, search, labelKeys])

    useEffect(() => {
      // Only null should reset the value
      if (!items || isUndefined(value)) return

      const item = items.find((item) =>
        isEqual(valueKey ? get(item, valueKey) : item, value)
      )

      setValue(item, true)
    }, [items, value])

    useEffect(() => {
      if (
        items &&
        display &&
        virtualListRef.current &&
        currentFocusIndex > -1 &&
        currentFocusIndex < items.length
      ) {
        virtualListRef.current.scrollToItem(currentFocusIndex)
      }
    }, [display, currentFocusIndex, items, virtualListRef.current])

    const defaultRenderItem = useCallback(
      (item) => <span>{getAutocompleteLinkLabel(item, labelKeys)}</span>,
      [labelKeys]
    )

    const showOptions = useCallback(() => {
      setDisplay(true)
      setCurrentFocusIndex(0)
      setSearch(
        currentValueObject ? getLabel(currentValueObject[labelKey]) : ''
      )
    }, [currentValueObject, optionsContainerRef.current])

    useEffect(() => {
      if (
        display &&
        optionsContainerRef.current &&
        optionsContainerRef.current.scrollIntoView
      ) {
        optionsContainerRef.current.scrollIntoView({
          behavior: 'smooth',
          block: 'nearest'
        })
      }
    }, [display])

    const hideOptions = useCallback(() => {
      if (value && items) {
        const itemIndex =
          typeof value !== 'undefined'
            ? items.findIndex((item) =>
                isEqual(valueKey ? get(item, valueKey) : item, value)
              )
            : -1
        setCurrentFocusIndex(itemIndex)
      } else {
        setCurrentFocusIndex(-1)
      }
      setDisplay(false)
    }, [value, items])

    const setValue = useCallback(
      (details, auto) => {
        setDisplay(false)
        setCurrentFocusIndex(-1)
        setCurrentValueObject(details)
        setSearch(details ? details[labelKey] : '')

        const nextValue = details
          ? valueKey
            ? get(details, valueKey)
            : details
          : null

        if (onChangeValue) onChangeValue(details)
        if (onChange && (!auto || value !== nextValue)) onChange(nextValue)
      },
      [
        labelKey,
        valueKey,
        setDisplay,
        setCurrentFocusIndex,
        setCurrentValueObject,
        setSearch,
        onChangeValue,
        onChange,
        value
      ]
    )

    const handleChange = useCallback(
      (event) => {
        showOptions()
        setSearch(event.target.value)
      },
      [showOptions, setSearch]
    )

    const handleBlur = useCallback(
      (event) => {
        if (!display) return
        if (onBlur) onBlur(event)

        const item = filteredItems.find(
          (item) => getLabel(item[labelKey]) === search
        )
        setValue(item, true)

        setDisplay(false)
      },
      [display, onBlur, filteredItems, labelKey, search]
    )

    const handleFocus = useCallback(
      (event) => {
        if (onFocus) onFocus(event)

        if (!disabled) showOptions()
      },
      [showOptions, onFocus, disabled]
    )

    const handleKeyDown = useCallback(
      (event) => {
        if (onKeyDown) onKeyDown(event)
        if (event.key === 'ArrowUp') {
          event.preventDefault()
          if (!display) {
            return showOptions()
          }
          setCurrentFocusIndex(
            Math.abs(
              (currentFocusIndex - 1 + filteredItems.length) %
                filteredItems.length
            )
          )
        } else if (event.key === 'ArrowDown') {
          event.preventDefault()
          if (!display) {
            return showOptions()
          }
          setCurrentFocusIndex(
            Math.abs(
              (currentFocusIndex + 1 + filteredItems.length) %
                filteredItems.length
            )
          )
        } else if (event.key === 'Enter' && filteredItems[currentFocusIndex]) {
          event.preventDefault()
          setValue(filteredItems[currentFocusIndex])
        } else if (event.key === 'Escape') {
          event.preventDefault()
          hideOptions()
        }
      },
      [
        display,
        currentFocusIndex,
        filteredItems,
        showOptions,
        hideOptions,
        setValue
      ]
    )

    const itemData = useMemo(
      () => ({
        currentFocusIndex,
        items: filteredItems,
        renderItem: renderItem || defaultRenderItem,
        onClick: setValue
      }),
      [filteredItems, renderItem, defaultRenderItem, currentFocusIndex]
    )

    const clearSearch = () => {
      if (search.length > 0) {
        setSearch('')
        setValue(null, true)
      }
    }

    // Call hook passing in the ref and a function to call on outside click
    useOnClickOutside(autoCompleteWrapperRef, handleBlur)

    useImperativeHandle(ref, () => ({
      focus: () => {
        if (inputRef.current) inputRef.current.focus()
      },
      blur: () => {
        if (inputRef.current) inputRef.current.blur()
      }
    }))

    return (
      <div
        ref={autoCompleteWrapperRef}
        onKeyDown={handleKeyDown}
        className={cx('ublique-autocomplete', { display, disabled }, className)}
      >
        <CustomInput
          ref={ref}
          tabIndex={0}
          autoComplete='off'
          {...props}
          onFocus={handleFocus}
          onChange={handleChange}
          value={
            display
              ? search
              : currentValueObject
              ? getLabel(currentValueObject[labelKey])
              : ''
          }
        >
          {(inputProps) => (
            <Fragment>
              {children instanceof Function ? (
                children(inputProps, inputRef, clearSearch)
              ) : (
                <input ref={inputRef} {...inputProps} />
              )}
              {display ? (
                <div
                  className='bx--list-box__menu items'
                  ref={optionsContainerRef}
                >
                  <List
                    ref={virtualListRef}
                    itemSize={40}
                    itemData={itemData}
                    itemCount={filteredItems.length}
                    height={Math.min(filteredItems.length * 40, 300)}
                  >
                    {AutocompleteOption}
                  </List>
                </div>
              ) : null}
            </Fragment>
          )}
        </CustomInput>
      </div>
    )
  }
)

Autocomplete.defaultProps = {
  valueKey: 'value',
  labelKey: 'label',
  labelText: '',
  getLabel: (label) => label
}

Autocomplete.propTypes = {
  /** The TextInput id */
  id: PropTypes.string,
  /** The TextInput label text */
  labelText: PropTypes.string,
  /** The list of data items that will be used to search and select the item for autocompletion */
  options: PropTypes.array,
  items: PropTypes.array,
  /** Item key that will be displayed in the input and for the options (unless renderItem is specified). */
  labelKey: PropTypes.string,
  /** Item key that will be used to match the `value` prop with the provided options */
  valueKey: PropTypes.string,
  /** Function for rendering a custom option. (item) => React.node */
  renderItem: PropTypes.func,
  /** Function called on value change (with the valueKey property of the selected item as parameter) */
  onChange: PropTypes.func,
  /** Function called on value change (with the selected item object as parameter) */
  onChangeValue: PropTypes.func,
  /**  */
  getLabel: PropTypes.func
}

export default Autocomplete

const addSpaceBetweenKeys = (list, idx) => {
  if (list.length > 0 && idx < list.length - 1) {
    return ' - '
  } else return ''
}

const getAutocompleteLinkLabel = (data, labelKey) => {
  if (isEmpty(data)) return ''
  if (isArray(labelKey)) {
    if (isEmpty(labelKey))
      throw new Error(`${data?.type} - Labelkey cannot be empty!`)

    const labelKeys = labelKey.filter((key) => data[key]).map((k) => data[k])

    return labelKeys.length > 1 ? labelKeys.join(' - ') : first(labelKeys)
  } else {
    return data[labelKey]
  }
}
